import { useCallback } from 'react';
import { NavigateOptions, useNavigate, generatePath } from 'react-router-dom';
import { useProjectSiteParams } from './use-project-site-params';

export const useProjectSiteNavigation = () => {
  const navigate = useNavigate();
  const { projectSlug, siteSlug, projectId, siteId } = useProjectSiteParams();

  const buildContextPath = useCallback((path: string, pSlug: string, sSlug: string): string => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `/${pSlug}/${sSlug}/${cleanPath}`;
  }, []);

  const buildPath = useCallback(
    (path: string, params?: Record<string, string | number>) => {
      if (!projectSlug || !siteSlug) return path;

      const hasParams = path.includes(':projectId') && path.includes(':siteId');
      return hasParams
        ? generatePath(path, { projectId: projectSlug, siteId: siteSlug, ...params })
        : buildContextPath(path, projectSlug, siteSlug);
    },
    [projectSlug, siteSlug, buildContextPath]
  );

  const navigateWithContext = useCallback(
    (
      path: string,
      params?: Record<string, string | number>,
      options?: NavigateOptions,
      search?: Record<string, string | number>
    ) => {
      if (!projectSlug || !siteSlug) {
        navigate('/', options);
        return;
      }

      let target = buildPath(path, params);
      if (search) {
        const query = new URLSearchParams(
          Object.entries(search).map(([key, value]) => [key, String(value)])
        ).toString();
        if (query) {
          target += `?${query}`;
        }
      }
      navigate(target, options);
    },
    [projectSlug, siteSlug, navigate, buildPath]
  );

  return {
    navigateWithContext,
    buildPath,
    projectId,
    siteId,
    projectSlug,
    siteSlug
  };
};
